/**
 * Gateways de pagamento — o que existe hoje e o que falta para conciliar.
 *
 * O protótipo desenha um assistente de três passos que "conecta" o gateway. Não
 * portei o assistente: a credencial de um adquirente é segredo do cliente, tem
 * de ir para um cofre no servidor — como `canais_credenciais`, que tem RLS sem
 * política nenhuma e só o `service_role` lê — e o pareamento é ação de quem
 * administra a conta. Um botão que diz "Conectar" e só emite um aviso seria
 * pior que a ausência dele.
 *
 * O que a tela mostra de verdade é o que a clínica já tem: a taxa e o prazo de
 * cada forma de pagamento, que são exatamente os números que entram no líquido
 * e no saldo.
 */

import { Badge, Card, Icon, KpiCard } from '@ds/core';
import { Alert } from '@ds/feedback';
import { diaIso, moeda } from '@/comuns/formato';
import type { MetodoDePagamento, Movimentacao } from '@/data/tipos';

export interface GatewaysProps {
    metodos: MetodoDePagamento[];
    /** Movimentações do mês de referência, para o custo real de taxa. */
    movimentacoes: Movimentacao[];
    mes: string;
}

export function Gateways({ metodos, movimentacoes, mes }: GatewaysProps) {
    const comTaxa = movimentacoes.filter((m) => m.taxaCentavos > 0);
    const custoDeTaxa = comTaxa.reduce((s, m) => s + m.taxaCentavos, 0);
    const brutoComTaxa = comTaxa.reduce((s, m) => s + m.valorCentavos, 0);
    const taxaEfetiva = brutoComTaxa > 0 ? (custoDeTaxa / brutoComTaxa) * 100 : 0;

    const aPrazo = metodos.filter((m) => m.prazoDias > 0);
    const prazoMedio = aPrazo.length > 0
        ? Math.round(aPrazo.reduce((s, m) => s + m.prazoDias, 0) / aPrazo.length)
        : 0;

    /* Quanto ainda não caiu: crédito em trinta dias é receita já reconhecida e
       dinheiro que a clínica não tem. A régua é HOJE, não a data da baixa — o
       que foi recebido no dia 2 e liquidou no dia 5 já está na conta. */
    const hoje = diaIso();
    const aReceber = movimentacoes.filter((m) => m.tipo === 'receber' && (!m.liquidadoEm || m.liquidadoEm > hoje));
    const emTransito = aReceber.reduce((s, m) => s + m.liquidoCentavos, 0);

    return (
        <>
            <div className="vec-stagger" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 12 }}>
                <KpiCard label="Custo de taxa no mês" value={moeda(custoDeTaxa)} hint={brutoComTaxa > 0 ? `sobre ${moeda(brutoComTaxa)} recebidos com taxa` : 'nenhuma baixa com taxa'} />
                <KpiCard label="Taxa efetiva" value={`${taxaEfetiva.toFixed(2).replace('.', ',')}%`} hint="taxa cobrada ÷ valor bruto" />
                {/* Média simples do cadastro, e o rótulo diz isso: ponderar
                    pelo volume do mês daria outro número, e um "D+11" sem
                    origem declarada vira promessa de data. */}
                <KpiCard label="Prazo médio de repasse" value={prazoMedio > 0 ? `D+${prazoMedio}` : 'na hora'} hint={`média simples de ${aPrazo.length} formas a prazo`} />
                <KpiCard label="Em trânsito" value={moeda(emTransito)} hint="recebido e ainda não liquidado" deltaTone={emTransito > 0 ? 'up' : 'neutral'} />
            </div>

            {/* A conciliação automática depende de integração com o adquirente,
                que depende de credencial em cofre. Dizer isso é mais útil que
                desenhar um assistente que não conecta nada. */}
            <Alert tone="info" title="Conciliação automática ainda não está ligada" dismissible={false}>
                A taxa e o prazo abaixo vêm do cadastro da clínica e já entram no líquido, no saldo e no extrato.
                Puxar o repasse direto do adquirente exige guardar a credencial num cofre no servidor — nunca no
                navegador — e o pareamento é feito por quem administra a conta. Até lá, a baixa é registrada à mão
                e a taxa é calculada pela forma de pagamento.
            </Alert>

            <Card title="Formas de pagamento e o que elas custam" subtitle={`Referência: ${mes}`} padding={18}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {metodos.map((m) => {
                        const doMetodo = movimentacoes.filter((x) => x.metodo === m.nome);
                        const bruto = doMetodo.reduce((s, x) => s + x.valorCentavos, 0);
                        const taxa = doMetodo.reduce((s, x) => s + x.taxaCentavos, 0);
                        return (
                            <div
                                key={m.id}
                                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 12px', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', flexWrap: 'wrap' }}
                            >
                                <Icon name={m.prazoDias > 0 ? 'credit-card' : 'banknote'} size={16} style={{ color: 'var(--neutral-500)', flex: '0 0 auto' }} />
                                <span style={{ flex: 1, minWidth: 140, fontFamily: 'var(--font-ui)', fontSize: 13.5, fontWeight: 600 }}>{m.nome}</span>
                                <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{m.conta ?? 'sem conta de destino'}</span>
                                <Badge tone={m.prazoDias > 0 ? 'warning' : 'success'}>{m.prazoDias > 0 ? `D+${m.prazoDias}` : 'na hora'}</Badge>
                                <span style={{ width: 68, textAlign: 'right', fontSize: 12.5, color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
                                    {m.taxaPercentual > 0 ? `${String(m.taxaPercentual).replace('.', ',')}%` : 'sem taxa'}
                                </span>
                                <span style={{ width: 150, textAlign: 'right', fontSize: 12.5, fontVariantNumeric: 'tabular-nums', color: bruto > 0 ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>
                                    {bruto > 0 ? `${moeda(bruto)} · taxa ${moeda(taxa)}` : 'sem baixa no mês'}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </Card>
        </>
    );
}
